import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getOperationType, rowStyle } from '../../components/common-constants';
import { Col, Divider, Empty, Form, InputNumber, Row, Select, Space, message, Tooltip, Button, Input } from 'antd';
import { getFormItems } from '../../components/form/formComponents';
import { fetchData, saveData } from '../../service/dataServices';
import moment from 'moment';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import { hasWritePermission } from '../../../components/Authorization/hasWritePermission';
import { EditOutlined } from '@ant-design/icons';

const { Option } = Select;

const SaveTreatment = () => {
  const PATH = '/api/treatments';
  const { id } = useParams();
  const operation = getOperationType(id);
  const hasWritePerm = hasWritePermission("treatment");

  const [formObject] = Form.useForm();
  const [form] = Form.useForm();
  const [mainImageFileList, setMainImageFileList] = useState([]);
  const [imageList, setImageList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [initialData, setInitialData] = useState();
  const [dropdowns, setDropdowns] = useState();
  const [initialOtherDimensions, setInitialOtherDimensions] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [objectFilters, setObjectFilters] = useState({
    page: 0,
    size: 20,
  });

  const {
    getDropdownFormItem,
    getMultiselectFormItem,
    getInputFormItem,
    getInputNumberFormItem,
    getDatePickerFormItem,
    getImageListFormItem,
    getMainImageFormItem,
    getTextAreaFormItem,
    getTextAreaFormItemWoLength,
    getFormSubmitButton,
    getOtherDimensionsFormItems,
    getDocumentUploadFormItem,
  } = getFormItems(dropdowns, hasWritePerm);

  fetchData('/api/dropdowns', setDropdowns);
  if (id) {
    fetchData(PATH + '/' + id, setInitialData);
  }

  const objectResponse = fetchData('/api/objects', data => setObjects(data.content), objectFilters);
  const saveMutation = saveData(PATH, operation.method);
  const fetchObjects = fetchData('/api/objects/' + selectedObjectId, setSelectedObject, null, false);
  const fetchNextReportId = fetchData(PATH + '/reportId', (p) => ({}), null, false);

  useEffect(() => {
    if (selectedObjectId) {
      fetchObjects.refetch().then(resp => {
        let data = resp.data.data;
        const found = objects.find(o => o.id === data.id);
        if (!found) {
          setObjects(prev => [...prev, {id: data.id, inventoryNumber: data.inventoryNumber }])
        }
      })
    }
  }, [selectedObjectId]);

  useEffect(() => {
    if (selectedObject) {
      setInitialOtherDimensions(selectedObject.otherDimensions || []);
      if (selectedObject.mainImage) {
        setMainImageFileList([selectedObject.mainImage]);
      } else {
        setMainImageFileList([]);
      }
      formObject.setFieldsValue(selectedObject);
    }
  }, [selectedObject]);

  useEffect(() => {
    if (initialData && dropdowns) {
      setSelectedObjectId(initialData.objectId);
      setReportId(initialData.reportId);
      if (initialData.imageList) setImageList(initialData.imageList);
      if (initialData.fileList) setFileList(initialData.fileList);
      if (initialData.treatmentDate) {
        form.setFieldsValue({
          ...initialData,
          treatmentDate: moment(initialData.treatmentDate),
        });
      } else {
        form.setFieldsValue(initialData);
      }
    }
  }, [initialData, dropdowns]);

  const onSearch = val => {
    if (val.length >= 4) {
      setObjectFilters((prev) => {
        return ({ ...prev, inventoryNumber: val });
      });
    }
  };

  const onClearSelectedObject = () => {
    setSelectedObjectId(null);
    setSelectedObject(null);
    formObject.resetFields();
    setInitialOtherDimensions([]);
    setMainImageFileList([]);
  };

  const onFinish = (values) => {
    if (!reportId) {
      message.error('Report ID field is required');
    } else {
      const dataToSend = {
        ...values,
        objectId: selectedObjectId,
        reportId: reportId,
        imageList: imageList ? imageList : undefined,
        fileList: fileList ? fileList : undefined,
      };
      if (id) {
        saveMutation.mutate({ dataToSend, id });
      } else {
        saveMutation.mutate({ dataToSend });
      }
    }
  };

  return (
    <CustomAddNewPageContent title={operation.type + ' Treatment'} subTitle="Treatments">
      <Row style={{ marginLeft: 20, marginRight: 20 }}>
        <Col span={12}>
          <Select
            showSearch
            placeholder="Find object by inventory number"
            allowClear
            value={selectedObjectId}
            onClear={onClearSelectedObject}
            onSelect={value => setSelectedObjectId(value)}
            onSearch={onSearch}
            filterOption={false}
            loading={objectResponse.isFetching}
            style={{ width: '36vh' }}
          >
            {
              objects.map(option => (
                <Option value={option.id} key={option.id}>{option.inventoryNumber}</Option>
              ))
            }
          </Select>
        </Col>
        <Col span={12}>
          <Row justify="end" style={{ alignItems: "center" }}>
            <Space>
              ReportId:
              <InputNumber
                style={{ width: "auto" }}
                disabled={!hasWritePermission}
                placeholder="Report Id"
                allowClear
                value={reportId}
                onChange={setReportId}
                prefix={(
                  <Tooltip title="Fill automatically">
                    <Button
                      style={{ padding: 0 }}
                      type="link"
                      icon={<EditOutlined/>}
                      onClick={() => {
                        fetchNextReportId.refetch().then(resp => {
                          if (resp.data && resp.data.data) setReportId(resp.data.data);
                        });
                      }}
                    />
                  </Tooltip>)}
              />
            </Space>
          </Row>
        </Col>
      </Row>
      <Divider/>

      <Form
        form={formObject}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
            {getMainImageFormItem('mainImage', true, 'objects', mainImageFileList, setMainImageFileList)}
            {mainImageFileList && mainImageFileList.length === 0 && (
              <div>
                <Space direction="vertical">
                  <Empty description={false}/>
                </Space>
              </div>
            )}
          </Col>
          <Col span={8}>
            {getInputFormItem('inventoryNumber', 'Inventory Number', false, 'text', true)}
            {getInputFormItem('objectName', 'Object Name', false, 'text', true)}
            {getInputFormItem('objectTitle', 'Object Title', false, 'text', true)}
            {getMultiselectFormItem('material', 'Material', true)}
            {getMultiselectFormItem('technique', 'Technique', true)}
          </Col>
          <Col span={8}>
            {getInputNumberFormItem('length', 'Length', 'cm', true)}
            {getInputNumberFormItem('width', 'Width', 'cm', true)}
            {getInputNumberFormItem('height', 'Height', 'cm', true)}
            {getInputNumberFormItem('weight', 'Weight', 'gr', true)}
            {getOtherDimensionsFormItems(initialOtherDimensions, true)}
          </Col>
        </Row>
      </Form>
      <Divider/>
      <Form
        form={form}
        labelCol={{ span: 16 }}
        wrapperCol={{ span: 16 }}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        <Row style={rowStyle}>
          <Col span={8}>
            {getInputFormItem('locationOfTreatment', 'Location Of Treatment')}
          </Col>
          <Col span={8}>
            {getDropdownFormItem('treatmentCategory', 'Treatment Category')}
          </Col>
          <Col span={8}>
            {getDropdownFormItem('treatmentPurpose', 'Treatment Purpose')}
          </Col>
          <Col span={8}>
            {getMultiselectFormItem('damageType', 'Damage Type')}
          </Col>
          <Col span={8}>
            {getInputFormItem('treatmentName', 'Treatment Name')}
          </Col>
          <Col span={8}>
            {getDropdownFormItem('treatmentDegree', 'Treatment Degree')}
          </Col>
          <Col span={8}>
            {getInputFormItem('treatmentPosition', 'Treatment Position')}
          </Col>
        </Row>
        <Divider/>
        <Row style={rowStyle}>
          <Col span={12}>
            {getTextAreaFormItemWoLength('treatmentProcess', 'Treatment Process')}
          </Col>
          <Col span={12}>
            {getTextAreaFormItemWoLength('recommendations', 'Recommendations')}
          </Col>
        </Row>
        <Row style={rowStyle}>
          <Col span={24} style={{ marginTop: '2vh' }}>
              {getDocumentUploadFormItem("treatmentDocumentation",
                !hasWritePerm,
                'treatment-documents',
                fileList,
                setFileList )}
          </Col>
        </Row>
        <Divider />
        <Row style={rowStyle}>
          {getImageListFormItem('imageList', false, 'treatments', imageList, setImageList)}
        </Row>
        <Divider/>
        <Row style={rowStyle}>
          <Col span={12}>
            {getDatePickerFormItem('treatmentDate', 'Treatment Date')}
          </Col>
          <Col span={12}>
            {getInputFormItem('treatedBy', 'Treated By')}
          </Col>
        </Row>
        <Divider/>
        {getFormSubmitButton(!selectedObject)}

      </Form>

    </CustomAddNewPageContent>
  );
};

export default SaveTreatment;
